import { ErrorBoundary } from 'src/components/ErrorBoundary';
import * as React from 'react';
import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';
import { StyledWrapper } from 'src/components/UserManagement/ListUsers/ListUsers.styled';
import { Table, TablePagination } from 'src/components/Table';
import { useGetSimCardDetails } from 'src/hooks/api/use-get-sim-card-details';
import { useNotification, useSearch } from 'src/hooks';
import { useFindDeviceBySimCardId } from 'src/hooks/api/use-find-device-by-sim-card-id';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UI_ROUTES } from 'src/constants';
import { ACTIVATED_SIM_STATUS } from 'src/utils/helpers/sim-health';
import { SelectChangeEvent } from '@mui/material';

import { CircularProgress, PageHeader, SelectInput } from 'src/components/atoms';
import { TABLE_COLUMNS } from './table-settings';
import { StyledInfoTooltip, StyledTitle } from './SimPage.styled';

const ALL_SIMS = 'ALL';
// only show sims in use by default, unused ones are usually still in inventory or test ready
const DEFAULT_SIM_STATUS = ACTIVATED_SIM_STATUS;

export const SimPage: React.FunctionComponent = () => {
  const [searchParams, setSearchParams] = useSearch();
  const simStatus = searchParams.sim_status || DEFAULT_SIM_STATUS;
  const { data, isLoading, isFetching } = useGetSimCardDetails({
    page: Number(searchParams.page) || 1,
    status: simStatus === ALL_SIMS ? undefined : simStatus,
  });

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueErrorNotification, enqueueInfoNotification } = useNotification();
  const { findDeviceBySimCardIdAsync } = useFindDeviceBySimCardId();

  const handleRowClick = async (e: React.SyntheticEvent<HTMLTableRowElement>) => {
    const event = e as React.MouseEvent<HTMLTableRowElement, MouseEvent>;
    const simId = event.currentTarget.dataset?.id;

    if (!simId) return;

    // check if ctrl or cmd key is pressed
    const openInNewTab = event.ctrlKey || event.metaKey;

    try {
      const device = await findDeviceBySimCardIdAsync(simId);

      if (!device) {
        enqueueInfoNotification(t('sim.table.no.device'));
        return;
      }

      const devicePath = `${UI_ROUTES.ORG_BROWSER}/${device.organisations[0]}/${device.uuid}`;

      if (openInNewTab) {
        window.open(`${window.location.origin}${devicePath}`, '_blank');
        return;
      }

      navigate(devicePath);
    } catch (error) {
      enqueueErrorNotification((error as Error)?.message || t('sim.table.device.error'));
    }
  };
  if (isLoading || isFetching) {
    return <CircularProgress />;
  }

  const handleStatusChange = (event: SelectChangeEvent<unknown>) => {
    setSearchParams((prevState) => ({
      ...prevState,
      sim_status: event.target.value as string,
      page: 1,
    }));
  };

  const STATUS_OPTIONS = [
    { label: t('sim.table.all.sims'), value: ALL_SIMS },
    ...(data?.metadata.statuses ?? []).map((status) => ({ label: upperFirst(lowerCase(status)), value: status })),
  ];
  // the table reads each row's id from data-id on click
  const tableData = (data?.data || []).map((sim) => ({ ...sim, id: sim.sim_id }));

  return (
    <ErrorBoundary componentId="SimPage">
      <PageHeader
        title="navigation.sim.detail"
        customTitle={
          <StyledTitle $fontWeight={500} $fontSize="22px">
            {t('navigation.sim.detail')}
            <StyledInfoTooltip title={t('sim.table.info')} />
          </StyledTitle>
        }
      >
        <SelectInput
          options={STATUS_OPTIONS}
          value={simStatus}
          onChange={handleStatusChange}
          name="filter-by-status"
          small
        />
      </PageHeader>

      <StyledWrapper>
        <Table
          columns={TABLE_COLUMNS}
          showSuccessState={!isLoading}
          showLoadingState={isLoading || isFetching}
          data={tableData}
          loadingStateRowLength={50}
          stickyHeader
          handleRowClick={handleRowClick}
        />
        <TablePagination totalPages={data?.total_pages || 1} />
      </StyledWrapper>
    </ErrorBoundary>
  );
};
